import getCart from '../lib/getCart';
import updateCartCount from '../lib/updateCartCount';
import renderMinicart from '../lib/renderMinicart';
import { CLASSES, ARIAS, FBT } from '../utils/constants';

/** DOM selectors for the Frequently Bought Together section */
const FBT_SELECTORS = {
  SECTION: '[data-frequently-bought-together]',
  ATC_BTN: '[data-fbt-add-to-cart]',
  TOTAL_PRICE: '[data-fbt-total-price]',
  VARIANT_SELECTOR: '[data-fbt-variant-selector]',
  DROPDOWN_TOGGLE: '[data-fbt-dropdown-toggle]',
  DROPDOWN_LIST: '[data-fbt-dropdown-list]',
  VARIANT_OPTION: '[data-fbt-variant-option]'
};

const FALSE_STRING = 'false';

/** Close all open variant dropdowns within a section */
const closeAllDropdowns = (section) => {
  section.querySelectorAll(FBT_SELECTORS.DROPDOWN_LIST).forEach((l) => {
    l.classList.add(CLASSES.HIDDEN);
    l.closest(FBT_SELECTORS.VARIANT_SELECTOR)
      ?.querySelector(FBT_SELECTORS.DROPDOWN_TOGGLE)
      ?.setAttribute(ARIAS.EXPANDED, FALSE_STRING);
  });
};

/**
 * Initialise the Frequently Bought Together section.
 * Handles variant selection, total price calculation, and add-to-cart.
 */
const initFrequentlyBoughtTogether = () => {
  const sections = document.querySelectorAll(FBT_SELECTORS.SECTION);
  if (!sections.length) return;

  sections.forEach((section) => {
    const atcBtn = section.querySelector(FBT_SELECTORS.ATC_BTN);
    const totalPriceEl = section.querySelector(FBT_SELECTORS.TOTAL_PRICE);
    const dropdownSelectors = section.querySelectorAll(FBT_SELECTORS.VARIANT_SELECTOR);

    if (!atcBtn || !totalPriceEl) return;

    /**
     * Recalculate the combined price of all selected variants
     * and update the total price display.
     */
    const updateTotalPrice = () => {
      const priceEls = section.querySelectorAll('[data-fbt-price]');
      if (!priceEls.length) return;

      // Get the raw price (in cents) for a given product card
      const getSelectedPriceRaw = (cardIndex) => {
        const selector = section.querySelector(`[data-fbt-variant-selector="${cardIndex}"]`);
        if (!selector) {
          // No variant selector — fall back to the first variant from the JSON data
          const dataEl = section.querySelector(`[data-fbt-product-${cardIndex}-data]`);
          if (dataEl) {
            const variants = JSON.parse(dataEl.textContent);
            return variants[0]?.price || 0;
          }
          return 0;
        }
        const selected = selector.querySelector('[aria-selected="true"]');
        if (selected) return parseInt(selected.dataset.variantPriceRaw, 10);

        // Fallback if aria-selected isn't set yet — use the first variant from JSON data
        const dataEl = section.querySelector(`[data-fbt-product-${cardIndex}-data]`);
        if (dataEl) {
          const variants = JSON.parse(dataEl.textContent);
          return variants[0]?.price || 0;
        }
        return 0;
      };

      let total = 0;
      priceEls.forEach((el) => {
        total += getSelectedPriceRaw(el.dataset.fbtPrice);
      });

      // Format using the store's active currency, fallback to GBP
      const formatter = window.Shopify?.currency?.active
        ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: window.Shopify.currency.active })
        : null;

      totalPriceEl.textContent = formatter
        ? formatter.format(total / FBT.CENTS_DIVISOR)
        : `£${(total / FBT.CENTS_DIVISOR).toFixed(FBT.DECIMAL_PLACES)}`;
    };

    /**
     * Handle variant selection — update the UI (label, price, swatch, image)
     * and store the selected variant ID on the ATC button.
     */
    const selectVariant = (option, cardIndex) => {
      if (option.dataset.variantAvailable === FALSE_STRING) return;

      const selector = option.closest(FBT_SELECTORS.VARIANT_SELECTOR);
      const list = selector.querySelector(FBT_SELECTORS.DROPDOWN_LIST);
      const label = section.querySelector(`[data-fbt-selected-label="${cardIndex}"]`);
      const priceEl = section.querySelector(`[data-fbt-price="${cardIndex}"]`);
      const swatchEl = section.querySelector(`[data-fbt-swatch="${cardIndex}"]`);

      // Reset all options, then mark the selected one
      list.querySelectorAll(FBT_SELECTORS.VARIANT_OPTION).forEach((opt) => {
        opt.removeAttribute('aria-selected');
        opt.classList.remove('font-semibold');
      });
      option.setAttribute('aria-selected', 'true');
      option.classList.add('font-semibold');

      // Update visible label, price, and swatch colour
      if (label) label.textContent = option.dataset.variantTitle;
      if (priceEl) priceEl.textContent = option.dataset.variantPrice;
      if (swatchEl && option.dataset.variantSwatch) {
        swatchEl.style.backgroundColor = option.dataset.variantSwatch;
      }

      // Store variant ID on the ATC button for submission
      atcBtn.dataset[`variant${cardIndex}`] = option.dataset.variantId;

      // Swap the product card image if the variant has one
      // Clear srcset so the browser uses the new src instead of cached responsive sources
      if (option.dataset.variantImage) {
        const card = section.querySelector(`[data-fbt-card="${cardIndex}"]`);
        const img = card?.querySelector('img');
        if (img) {
          img.src = option.dataset.variantImage;
          img.srcset = option.dataset.variantImage;
        }
      }

      updateTotalPrice();

      // Close the dropdown after selection
      list.classList.add(CLASSES.HIDDEN);
      const toggle = selector.querySelector(FBT_SELECTORS.DROPDOWN_TOGGLE);
      toggle?.setAttribute(ARIAS.EXPANDED, FALSE_STRING);
    };

    // Bind toggle and selection events for each variant dropdown
    dropdownSelectors.forEach((selector) => {
      const toggle = selector.querySelector(FBT_SELECTORS.DROPDOWN_TOGGLE);
      const list = selector.querySelector(FBT_SELECTORS.DROPDOWN_LIST);
      const cardIndex = selector.dataset.fbtVariantSelector;

      if (!toggle || !list) return;

      toggle.addEventListener('click', () => {
        const isOpen = !list.classList.contains(CLASSES.HIDDEN);
        closeAllDropdowns(section);
        if (!isOpen) {
          list.classList.remove(CLASSES.HIDDEN);
          toggle.setAttribute(ARIAS.EXPANDED, 'true');
        }
      });

      list.querySelectorAll(FBT_SELECTORS.VARIANT_OPTION).forEach((option) => {
        option.addEventListener('click', () => selectVariant(option, cardIndex));
      });
    });

    // Add all selected variants to cart
    atcBtn.addEventListener('click', async () => {
      const items = [];
      for (let i = 1; i <= FBT.PRODUCT_COUNT; i += 1) {
        const variantId = atcBtn.dataset[`variant${i}`];
        if (!variantId) continue;
        items.push({ id: parseInt(variantId, 10), quantity: 1 });
      }

      const spinner = atcBtn.querySelector('[data-loading-spinner]');
      const label = atcBtn.querySelector('span');

      atcBtn.disabled = true;
      if (label) label.classList.add('opacity-0');
      if (spinner) spinner.classList.remove(CLASSES.HIDDEN);

      try {
        await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        const cart = await getCart();
        updateCartCount(cart.item_count);
        await renderMinicart();
        window.theme.minicart?.open({ announcement: window.theme.strings?.addedToCart });
      } catch (err) {
        console.error('FBT add to cart error:', err);
      } finally {
        atcBtn.disabled = false;
        if (label) label.classList.remove('opacity-0');
        if (spinner) spinner.classList.add(CLASSES.HIDDEN);
      }
    });

    // Set initial total on page load
    updateTotalPrice();
  });
};

// Close all dropdowns when clicking outside — bound once at module level
document.addEventListener('click', (evt) => {
  if (!evt.target.closest(FBT_SELECTORS.VARIANT_SELECTOR)) {
    document.querySelectorAll(FBT_SELECTORS.SECTION).forEach((section) => {
      closeAllDropdowns(section);
    });
  }
});

export default initFrequentlyBoughtTogether;
